import { useState, useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  History,
  RotateCcw,
  Plus,
  Trash2,
  Wifi,
  WifiOff,
  Power,
  PowerOff,
  Cable,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  Pencil,
  Settings,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useConfigVersions,
  useRestoreConfigVersion,
  useSingleStudioConfig,
} from "@/hooks/useStudioConfigs";
import type { UseConfigDataReturn } from "@/hooks/useConfigData";
import { DanteTab } from "./dante/DanteTab";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  ConnectionConfig,
  ConnectionStatus,
  DevicePort,
  DeviceStateStore,
  PhysicalConnectionInsert,
} from "@production-hub/shared";
import { getDevicePorts } from "@production-hub/shared";
import {
  useBridgeDeviceStore,
  useUpdateBridgeDeviceStore,
} from "@/hooks/useBridgeConnections";
import { useBridgePorts } from "@/hooks/useBridgePhysicalConnections";
import { CONNECTION_TYPE_LABELS, type ConnectionType } from "./connectionTypes";
import { StudioConnectionForm } from "./StudioConnectionForm";
import { VideoHubControl } from "./VideoHubControl";
import { AtemControl } from "./AtemControl";
import { DeviceStateEditor } from "./DeviceStateEditor";

interface ConfigEditorProps {
  configId: string;
  studioId: string;
  configData: UseConfigDataReturn;
  bridgeUrl?: string;
}

// ─── Status badge helper ─────────────────────────────────────

const getStatusBadge = (status?: ConnectionStatus) => {
  switch (status) {
    case "connected":
      return (
        <Badge variant="default" className="gap-1 bg-green-600">
          <Wifi className="h-3 w-3" />
          Connected
        </Badge>
      );
    case "connecting":
      return (
        <Badge variant="secondary" className="gap-1">
          Connecting...
        </Badge>
      );
    case "error":
      return (
        <Badge variant="destructive" className="gap-1">
          <WifiOff className="h-3 w-3" />
          Error
        </Badge>
      );
    default:
      return null;
  }
};

// ─── Device Setup View ───────────────────────────────────────

interface DeviceSetupViewProps {
  conn: ConnectionConfig & { status?: ConnectionStatus };
  configData: UseConfigDataReturn;
  bridgeUrl?: string;
  onBack: () => void;
}

const DeviceSetupView = ({
  conn,
  configData,
  bridgeUrl,
  onBack,
}: DeviceSetupViewProps) => {
  const status = conn.status;
  const isOnline = configData.mode === "online";

  // Online store hooks
  const bridgeStore = useBridgeDeviceStore(
    isOnline ? bridgeUrl : null,
    isOnline ? conn.id : undefined,
  );
  const updateBridgeStore = useUpdateBridgeDeviceStore(
    isOnline ? bridgeUrl : null,
  );

  // Determine store and update function
  const store: DeviceStateStore | undefined = isOnline
    ? bridgeStore.data
    : configData.getDeviceState(conn.id);

  const handleUpdateValue = (
    connectionId: string,
    path: string,
    value: string | number | boolean,
  ) => {
    if (isOnline) {
      updateBridgeStore.mutate({ connectionId, path, value });
    } else {
      configData.updateDeviceStateValue(connectionId, path, value);
    }
  };

  const isLoading = isOnline ? bridgeStore.isLoading : false;
  const isError = isOnline ? bridgeStore.isError : false;

  return (
    <div className="space-y-4" data-testid="device-setup-view">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          data-testid="device-setup-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">{conn.name}</h3>
            {status && getStatusBadge(status)}
          </div>
          <p className="text-sm text-muted-foreground">
            {CONNECTION_TYPE_LABELS[conn.connection_type as ConnectionType] ||
              conn.connection_type}{" "}
            &middot; {conn.host}
            {conn.port ? `:${conn.port}` : ""}
          </p>
        </div>
        {/* Online connect/disconnect */}
        {isOnline && status === "connected" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => configData.disconnectDevice?.(conn.id)}
            disabled={configData.isDisconnecting}
          >
            <PowerOff className="h-4 w-4 mr-1" />
            Disconnect
          </Button>
        )}
        {isOnline && status !== "connected" && status !== "connecting" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => configData.connectDevice?.(conn.id)}
            disabled={configData.isConnecting}
          >
            <Power className="h-4 w-4 mr-1" />
            Connect
          </Button>
        )}
      </div>

      {/* Control component based on type — show init form when offline with no store */}
      {!store && !isOnline ? (
        <DeviceStateEditor
          connectionType={conn.connection_type as ConnectionType}
          connectionId={conn.id}
          store={store}
          onSetStore={configData.setDeviceState}
          onUpdateValue={configData.updateDeviceStateValue}
        />
      ) : conn.connection_type === "videohub" ? (
        <VideoHubControl
          store={store}
          connectionId={conn.id}
          isLoading={isLoading}
          isError={isError}
          connectionStatus={isOnline ? status : undefined}
          onUpdateValue={handleUpdateValue}
        />
      ) : conn.connection_type === "atem" ? (
        <AtemControl
          store={store}
          connectionId={conn.id}
          isLoading={isLoading}
          isError={isError}
          connectionStatus={isOnline ? status : undefined}
          onUpdateValue={handleUpdateValue}
        />
      ) : (
        <DeviceStateEditor
          connectionType={conn.connection_type as ConnectionType}
          connectionId={conn.id}
          store={store}
          onSetStore={configData.setDeviceState}
          onUpdateValue={
            isOnline
              ? (cid, path, val) => handleUpdateValue(cid, path, val)
              : configData.updateDeviceStateValue
          }
        />
      )}
    </div>
  );
};

// ─── Main ConfigEditor ───────────────────────────────────────

export const ConfigEditor = ({
  configId,
  studioId,
  configData,
  bridgeUrl,
}: ConfigEditorProps) => {
  const { data: config } = useSingleStudioConfig(configId);
  const { data: versions = [] } = useConfigVersions(configId);
  const restoreVersion = useRestoreConfigVersion();
  const [restoreVersionId, setRestoreVersionId] = useState<string | null>(null);

  // Add dialogs
  const [isAddConnectionOpen, setIsAddConnectionOpen] = useState(false);
  const [isAddLayoutOpen, setIsAddLayoutOpen] = useState(false);
  const [isAddProfileOpen, setIsAddProfileOpen] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const [newProfileCodec, setNewProfileCodec] = useState("");
  const [newProfileWidth, setNewProfileWidth] = useState("");
  const [newProfileHeight, setNewProfileHeight] = useState("");
  const [newProfileBitrate, setNewProfileBitrate] = useState("");

  // Add wire dialog
  const [isAddWireOpen, setIsAddWireOpen] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "connection" | "layout" | "profile" | "wire";
    id: string;
    name: string;
  } | null>(null);

  // Device setup/edit views
  const [setupDeviceId, setSetupDeviceId] = useState<string | null>(null);
  const [editDeviceId, setEditDeviceId] = useState<string | null>(null);

  const handleRestore = async () => {
    if (!restoreVersionId) return;
    await restoreVersion.mutateAsync({
      configId,
      versionId: restoreVersionId,
      studioId,
    });
    setRestoreVersionId(null);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    switch (deleteTarget.type) {
      case "connection":
        configData.deleteConnection(deleteTarget.id);
        break;
      case "layout":
        configData.deleteLayout(deleteTarget.id);
        break;
      case "profile":
        configData.deleteProfile(deleteTarget.id);
        break;
      case "wire":
        configData.deletePhysicalConnection(deleteTarget.id);
        break;
    }
    setDeleteTarget(null);
  };

  const handleAddConnection = (data: Record<string, unknown>) => {
    configData.addConnection(data as never);
    setIsAddConnectionOpen(false);
  };

  const handleEditConnection = (data: Record<string, unknown>) => {
    if (!editDeviceId) return;
    configData.updateConnection(editDeviceId, data as never);
    setEditDeviceId(null);
  };

  const handleAddLayout = () => {
    if (!newLayoutName.trim()) return;
    configData.addLayout({ name: newLayoutName.trim() });
    setNewLayoutName("");
    setIsAddLayoutOpen(false);
  };

  const handleAddProfile = () => {
    if (!newProfileName.trim()) return;
    configData.addProfile({
      name: newProfileName.trim(),
      video_codec: newProfileCodec || undefined,
      video_width: newProfileWidth ? Number(newProfileWidth) : undefined,
      video_height: newProfileHeight ? Number(newProfileHeight) : undefined,
      video_bitrate: newProfileBitrate ? Number(newProfileBitrate) : undefined,
    });
    setNewProfileName("");
    setNewProfileCodec("");
    setNewProfileWidth("");
    setNewProfileHeight("");
    setNewProfileBitrate("");
    setIsAddProfileOpen(false);
  };

  // Port enumeration for wiring tab
  const bridgePortsQuery = useBridgePorts(
    configData.mode === "online" ? bridgeUrl : null,
  );

  const allPorts: DevicePort[] = useMemo(() => {
    if (configData.mode === "online") {
      return bridgePortsQuery.data ?? [];
    }
    const ports: DevicePort[] = [];
    for (const conn of configData.connections) {
      const store = configData.getDeviceState(conn.id);
      if (store) {
        ports.push(
          ...getDevicePorts(
            conn.id,
            conn.connection_type as ConnectionType,
            store,
          ),
        );
      }
    }
    return ports;
  }, [configData, bridgePortsQuery.data]);

  const connectionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const conn of configData.connections) {
      map.set(conn.id, conn.name);
    }
    return map;
  }, [configData.connections]);

  const portLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const port of allPorts) {
      map.set(
        `${port.connection_id}:${port.port_type}:${port.index}`,
        port.label,
      );
    }
    return map;
  }, [allPorts]);

  const getPortLabel = (
    connectionId: string,
    portType: string,
    portIndex: number,
  ) =>
    portLabelMap.get(`${connectionId}:${portType}:${portIndex}`) ??
    `${portType} ${portIndex}`;

  const handleAddWire = (data: PhysicalConnectionInsert) => {
    configData.addPhysicalConnection(data);
    setIsAddWireOpen(false);
  };

  // Find connection for setup view
  const setupConn = setupDeviceId
    ? configData.connections.find((c) => c.id === setupDeviceId)
    : null;

  // Find connection for edit dialog
  const editConn = editDeviceId
    ? configData.connections.find((c) => c.id === editDeviceId)
    : null;

  if (configData.isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading configuration...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="config-editor">
      <Tabs defaultValue="devices">
        <TabsList>
          <TabsTrigger value="devices" data-testid="editor-tab-devices">
            Devices ({configData.connections.length})
          </TabsTrigger>
          <TabsTrigger value="layouts" data-testid="editor-tab-layouts">
            Layouts ({configData.layouts.length})
          </TabsTrigger>
          <TabsTrigger value="profiles" data-testid="editor-tab-profiles">
            Profiles ({configData.profiles.length})
          </TabsTrigger>
          <TabsTrigger value="wiring" data-testid="editor-tab-wiring">
            Wiring ({configData.physicalConnections.length})
          </TabsTrigger>
          <TabsTrigger value="dante" data-testid="editor-tab-dante">
            Dante ({configData.dante.devices.length})
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="gap-1"
            data-testid="editor-tab-history"
          >
            <History className="h-3 w-3" />
            History ({versions.length})
          </TabsTrigger>
        </TabsList>

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-2">
          {setupConn ? (
            <DeviceSetupView
              conn={
                setupConn as ConnectionConfig & { status?: ConnectionStatus }
              }
              configData={configData}
              bridgeUrl={bridgeUrl}
              onBack={() => setSetupDeviceId(null)}
            />
          ) : (
            <>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => setIsAddConnectionOpen(true)}
                  disabled={configData.isMutating}
                  data-testid="add-connection-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Device
                </Button>
              </div>

              {configData.connections.length === 0 ? (
                <Card>
                  <CardContent className="py-6 text-center text-muted-foreground">
                    No studio devices configured
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-40">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {configData.connections.map((conn) => {
                        const status = (
                          conn as ConnectionConfig & {
                            status?: ConnectionStatus;
                          }
                        ).status;
                        const isGenericDevice =
                          conn.connection_type === "generic";

                        return (
                          <TableRow
                            key={conn.id}
                            data-testid={`connection-card-${conn.id}`}
                            data-connection-name={conn.name}
                          >
                            <TableCell>
                              <div>
                                <span
                                  className="font-medium"
                                  data-testid="connection-name"
                                >
                                  {conn.name}
                                </span>
                                {conn.management_url && (
                                  <a
                                    href={conn.management_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {CONNECTION_TYPE_LABELS[
                                conn.connection_type as ConnectionType
                              ] || conn.connection_type}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {conn.host}
                              {!isGenericDevice && conn.port
                                ? `:${conn.port}`
                                : ""}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {status && getStatusBadge(status)}
                                <Badge
                                  variant={
                                    conn.enabled ? "default" : "secondary"
                                  }
                                >
                                  {conn.enabled ? "Enabled" : "Disabled"}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {!isGenericDevice && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setSetupDeviceId(conn.id)}
                                    data-testid="setup-device-button"
                                  >
                                    <Settings className="h-3 w-3 mr-1" />
                                    Setup
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditDeviceId(conn.id)}
                                  data-testid="edit-connection-button"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    setDeleteTarget({
                                      type: "connection",
                                      id: conn.id,
                                      name: conn.name,
                                    })
                                  }
                                  className="text-destructive hover:text-destructive"
                                  data-testid="delete-connection-button"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Layouts Tab */}
        <TabsContent value="layouts" className="space-y-2">
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => setIsAddLayoutOpen(true)}
              disabled={configData.isMutating}
              data-testid="add-layout-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Layout
            </Button>
          </div>

          {configData.layouts.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-muted-foreground">
                No layouts in this configuration
              </CardContent>
            </Card>
          ) : (
            configData.layouts.map((layout) => (
              <Card key={layout.id} data-testid={`layout-card-${layout.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium" data-testid="layout-name">
                        {layout.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {layout.boxes?.length ?? 0} boxes &middot;{" "}
                        {layout.presets?.length ?? 0} presets
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setDeleteTarget({
                          type: "layout",
                          id: layout.id,
                          name: layout.name,
                        })
                      }
                      className="text-destructive hover:text-destructive"
                      data-testid="delete-layout-button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Profiles Tab */}
        <TabsContent value="profiles" className="space-y-2">
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => setIsAddProfileOpen(true)}
              disabled={configData.isMutating}
              data-testid="add-profile-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Profile
            </Button>
          </div>

          {configData.profiles.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-muted-foreground">
                No profiles in this configuration
              </CardContent>
            </Card>
          ) : (
            configData.profiles.map((profile) => (
              <Card key={profile.id} data-testid={`profile-card-${profile.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium" data-testid="profile-name">
                        {profile.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {profile.video_codec && `${profile.video_codec} `}
                        {profile.video_width &&
                          profile.video_height &&
                          `${profile.video_width}x${profile.video_height}`}
                        {profile.video_bitrate &&
                          ` @ ${profile.video_bitrate}kbps`}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setDeleteTarget({
                          type: "profile",
                          id: profile.id,
                          name: profile.name,
                        })
                      }
                      className="text-destructive hover:text-destructive"
                      data-testid="delete-profile-button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Wiring Tab */}
        <TabsContent value="wiring" className="space-y-2">
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => setIsAddWireOpen(true)}
              disabled={configData.isMutating}
              data-testid="add-wire-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Wire
            </Button>
          </div>

          {configData.physicalConnections.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                <Cable className="h-8 w-8 mb-2" />
                <p>No physical connections configured</p>
                <p className="text-sm">
                  Add wires to map how devices are cabled together
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source Device</TableHead>
                    <TableHead>Source Port</TableHead>
                    <TableHead className="w-8" />
                    <TableHead>Dest Device</TableHead>
                    <TableHead>Dest Port</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configData.physicalConnections.map((pc) => (
                    <TableRow key={pc.id} data-testid={`wire-row-${pc.id}`}>
                      <TableCell className="font-medium">
                        {connectionNameMap.get(pc.source_connection_id) ??
                          pc.source_connection_id}
                      </TableCell>
                      <TableCell>
                        {getPortLabel(
                          pc.source_connection_id,
                          pc.source_port_type,
                          pc.source_port_index,
                        )}
                      </TableCell>
                      <TableCell>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                      <TableCell className="font-medium">
                        {connectionNameMap.get(pc.dest_connection_id) ??
                          pc.dest_connection_id}
                      </TableCell>
                      <TableCell>
                        {getPortLabel(
                          pc.dest_connection_id,
                          pc.dest_port_type,
                          pc.dest_port_index,
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setDeleteTarget({
                              type: "wire",
                              id: pc.id,
                              name: `${connectionNameMap.get(pc.source_connection_id) ?? "?"} → ${connectionNameMap.get(pc.dest_connection_id) ?? "?"}`,
                            })
                          }
                          className="text-destructive hover:text-destructive"
                          data-testid="delete-wire-button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Dante Tab */}
        <TabsContent value="dante" className="space-y-2">
          <DanteTab configData={configData} />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-2">
          {versions.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-muted-foreground">
                No version history
              </CardContent>
            </Card>
          ) : (
            versions.map((version) => (
              <Card key={version.id}>
                <CardHeader className="py-3 pb-0">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">
                      Version {version.version}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(version.created_at), {
                          addSuffix: true,
                        })}
                      </span>
                      {version.version !== config?.version && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRestoreVersionId(version.id)}
                          disabled={restoreVersion.isPending}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Restore
                        </Button>
                      )}
                      {version.version === config?.version && (
                        <Badge variant="outline" className="text-xs">
                          Current
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Add Device Dialog */}
      <Dialog open={isAddConnectionOpen} onOpenChange={setIsAddConnectionOpen}>
        <DialogContent data-testid="add-connection-dialog">
          <DialogHeader>
            <DialogTitle>Add Device</DialogTitle>
          </DialogHeader>
          <StudioConnectionForm
            onSubmit={handleAddConnection}
            onCancel={() => setIsAddConnectionOpen(false)}
            isLoading={configData.isConnectionMutating}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog
        open={!!editDeviceId}
        onOpenChange={(open) => !open && setEditDeviceId(null)}
      >
        <DialogContent data-testid="edit-connection-dialog">
          <DialogHeader>
            <DialogTitle>Edit Device</DialogTitle>
          </DialogHeader>
          {editConn && (
            <StudioConnectionForm
              onSubmit={handleEditConnection}
              onCancel={() => setEditDeviceId(null)}
              isLoading={configData.isConnectionMutating}
              initialValues={{
                name: editConn.name,
                connection_type: editConn.connection_type as ConnectionType,
                host: editConn.host,
                port: editConn.port ?? undefined,
                model: editConn.model ?? "",
                management_url: editConn.management_url ?? "",
                enabled: editConn.enabled,
              }}
              submitLabel="Save Changes"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Layout Dialog */}
      <Dialog open={isAddLayoutOpen} onOpenChange={setIsAddLayoutOpen}>
        <DialogContent data-testid="add-layout-dialog">
          <DialogHeader>
            <DialogTitle>Add Layout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="layout-name">Name</Label>
              <Input
                id="layout-name"
                data-testid="layout-name-input"
                value={newLayoutName}
                onChange={(e) => setNewLayoutName(e.target.value)}
                placeholder="Main Layout"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsAddLayoutOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddLayout}
                disabled={!newLayoutName.trim() || configData.isMutating}
                data-testid="add-layout-submit"
              >
                Add Layout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Profile Dialog */}
      <Dialog open={isAddProfileOpen} onOpenChange={setIsAddProfileOpen}>
        <DialogContent data-testid="add-profile-dialog">
          <DialogHeader>
            <DialogTitle>Add Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="profile-name">Name *</Label>
              <Input
                id="profile-name"
                data-testid="profile-name-input"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder="1080p H.264"
              />
            </div>
            <div>
              <Label htmlFor="profile-codec">Video Codec</Label>
              <Input
                id="profile-codec"
                data-testid="profile-codec-input"
                value={newProfileCodec}
                onChange={(e) => setNewProfileCodec(e.target.value)}
                placeholder="h264"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="profile-width">Width</Label>
                <Input
                  id="profile-width"
                  data-testid="profile-width-input"
                  type="number"
                  value={newProfileWidth}
                  onChange={(e) => setNewProfileWidth(e.target.value)}
                  placeholder="1920"
                />
              </div>
              <div>
                <Label htmlFor="profile-height">Height</Label>
                <Input
                  id="profile-height"
                  data-testid="profile-height-input"
                  type="number"
                  value={newProfileHeight}
                  onChange={(e) => setNewProfileHeight(e.target.value)}
                  placeholder="1080"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="profile-bitrate">Video Bitrate (kbps)</Label>
              <Input
                id="profile-bitrate"
                data-testid="profile-bitrate-input"
                type="number"
                value={newProfileBitrate}
                onChange={(e) => setNewProfileBitrate(e.target.value)}
                placeholder="5000"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsAddProfileOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddProfile}
                disabled={!newProfileName.trim() || configData.isMutating}
                data-testid="add-profile-submit"
              >
                Add Profile
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Wire Dialog */}
      <Dialog open={isAddWireOpen} onOpenChange={setIsAddWireOpen}>
        <DialogContent data-testid="add-wire-dialog">
          <DialogHeader>
            <DialogTitle>Add Physical Wire</DialogTitle>
          </DialogHeader>
          <AddWireForm
            ports={allPorts}
            connections={configData.connections}
            onSubmit={handleAddWire}
            onCancel={() => setIsAddWireOpen(false)}
            isLoading={configData.isMutating}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-item-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "connection"
                ? `This will remove "${deleteTarget?.name}" from the studio's cloud device connections.`
                : `This will remove "${deleteTarget?.name}" from this configuration.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-item-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Confirmation */}
      <AlertDialog
        open={!!restoreVersionId}
        onOpenChange={(open) => !open && setRestoreVersionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new version with the data from the selected
              version. The current configuration will be updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}>
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ── Inline AddWireForm (cascading selects) ──

interface AddWireFormProps {
  ports: DevicePort[];
  connections: Array<{ id: string; name: string }>;
  onSubmit: (data: PhysicalConnectionInsert) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const AddWireForm = ({
  ports,
  connections,
  onSubmit,
  onCancel,
  isLoading,
}: AddWireFormProps) => {
  const [sourceConnectionId, setSourceConnectionId] = useState<string>("");
  const [sourcePortKey, setSourcePortKey] = useState<string>("");
  const [destConnectionId, setDestConnectionId] = useState<string>("");
  const [destPortKey, setDestPortKey] = useState<string>("");

  const outputPorts = useMemo(
    () => ports.filter((p) => p.direction === "output"),
    [ports],
  );
  const inputPorts = useMemo(
    () => ports.filter((p) => p.direction === "input"),
    [ports],
  );

  const sourceDeviceIds = useMemo(
    () => [...new Set(outputPorts.map((p) => p.connection_id))],
    [outputPorts],
  );
  const destDeviceIds = useMemo(
    () => [...new Set(inputPorts.map((p) => p.connection_id))],
    [inputPorts],
  );

  const connectionName = (id: string) =>
    connections.find((c) => c.id === id)?.name ?? id;

  const filteredSourcePorts = useMemo(
    () =>
      sourceConnectionId
        ? outputPorts.filter((p) => p.connection_id === sourceConnectionId)
        : [],
    [outputPorts, sourceConnectionId],
  );
  const filteredDestPorts = useMemo(
    () =>
      destConnectionId
        ? inputPorts.filter((p) => p.connection_id === destConnectionId)
        : [],
    [inputPorts, destConnectionId],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const sourcePort = outputPorts.find(
      (p) => `${p.connection_id}:${p.port_type}:${p.index}` === sourcePortKey,
    );
    const destPort = inputPorts.find(
      (p) => `${p.connection_id}:${p.port_type}:${p.index}` === destPortKey,
    );
    if (!sourcePort || !destPort) return;
    onSubmit({
      source_connection_id: sourcePort.connection_id,
      source_port_type: sourcePort.port_type,
      source_port_index: sourcePort.index,
      dest_connection_id: destPort.connection_id,
      dest_port_type: destPort.port_type,
      dest_port_index: destPort.index,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Source Device</Label>
        <Select
          value={sourceConnectionId}
          onValueChange={(v) => {
            setSourceConnectionId(v);
            setSourcePortKey("");
          }}
        >
          <SelectTrigger data-testid="wire-source-device">
            <SelectValue placeholder="Select source device" />
          </SelectTrigger>
          <SelectContent>
            {sourceDeviceIds.map((id) => (
              <SelectItem key={id} value={id}>
                {connectionName(id)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Source Port</Label>
        <Select
          value={sourcePortKey}
          onValueChange={setSourcePortKey}
          disabled={!sourceConnectionId}
        >
          <SelectTrigger data-testid="wire-source-port">
            <SelectValue placeholder="Select source port" />
          </SelectTrigger>
          <SelectContent>
            {filteredSourcePorts.map((p) => {
              const key = `${p.connection_id}:${p.port_type}:${p.index}`;
              return (
                <SelectItem key={key} value={key}>
                  {p.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-center">
        <ArrowRight className="h-5 w-5 text-muted-foreground" />
      </div>

      <div className="space-y-2">
        <Label>Destination Device</Label>
        <Select
          value={destConnectionId}
          onValueChange={(v) => {
            setDestConnectionId(v);
            setDestPortKey("");
          }}
        >
          <SelectTrigger data-testid="wire-dest-device">
            <SelectValue placeholder="Select destination device" />
          </SelectTrigger>
          <SelectContent>
            {destDeviceIds.map((id) => (
              <SelectItem key={id} value={id}>
                {connectionName(id)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Destination Port</Label>
        <Select
          value={destPortKey}
          onValueChange={setDestPortKey}
          disabled={!destConnectionId}
        >
          <SelectTrigger data-testid="wire-dest-port">
            <SelectValue placeholder="Select destination port" />
          </SelectTrigger>
          <SelectContent>
            {filteredDestPorts.map((p) => {
              const key = `${p.connection_id}:${p.port_type}:${p.index}`;
              return (
                <SelectItem key={key} value={key}>
                  {p.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!sourcePortKey || !destPortKey || isLoading}
          data-testid="wire-form-submit"
        >
          {isLoading ? "Creating..." : "Create Wire"}
        </Button>
      </div>
    </form>
  );
};
